import { getFirebaseMessaging, getToken, isSupported, onMessage, saveFcmToken } from './firebase';
import { isNativeApp } from '../utils/platform';
import { getPushDeviceId } from '../utils/pushDevices';
import { APP } from '../config/app';

/** v3 channel — native default ringtone (v2 was silent: Capacitor mistook "default" for res/raw) */
export const CHANNEL_ALERTS = 'ride_alerts_v3';
export const CHANNEL_LIVE = 'ride_live';
export const LIVE_NOTIF_ID = 77001;

let messagingInstance = null;
let nativeListenersBound = false;
let channelsReady = false;
let rideLivePlugin = null;
// The registration listener is bound once but must attribute new tokens to
// whoever is signed in NOW — not the uid captured on first bind (a different
// passenger on the same device would otherwise inherit the previous one's
// ride alerts). Updated on every initPassengerMessaging / cleared on logout.
let currentPushUid = null;
// Last token the plugin handed us, and whether the Firestore write landed. If it
// failed (offline at that moment) we retry on reconnect / next foreground so the
// server never keeps pushing to a token we couldn't save.
let pendingToken = null;
let pendingTokenSynced = false;
let tokenRetryBound = false;

/** This device, for its own entry in users/{uid}.pushDevices (utils/pushDevices.js). */
const pushDevice = () => {
  const deviceId = getPushDeviceId();
  return deviceId ? { deviceId, platform: isNativeApp() ? 'android' : 'web', appVersion: APP.version } : null;
};

const syncPendingToken = async () => {
  if (!pendingToken || pendingTokenSynced || !currentPushUid) return;
  try {
    await saveFcmToken(currentPushUid, pendingToken, pushDevice());
    pendingTokenSynced = true;
  } catch (e) {
    pendingTokenSynced = false;
    console.warn('FCM token re-sync failed, will retry:', e?.message);
  }
};

const bindTokenRetry = () => {
  if (tokenRetryBound || typeof window === 'undefined') return;
  tokenRetryBound = true;
  window.addEventListener('online', syncPendingToken);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncPendingToken();
  });
};

const ensureWebMessaging = async () => {
  if (!(await isSupported())) return null;
  if (!messagingInstance) messagingInstance = getFirebaseMessaging();
  return messagingInstance;
};

const getRideLivePlugin = async () => {
  if (!isNativeApp()) return null;
  if (rideLivePlugin) return rideLivePlugin;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    rideLivePlugin = registerPlugin('RideLiveNotification');
    return rideLivePlugin;
  } catch {
    return null;
  }
};

const ensureNativeChannels = async () => {
  if (channelsReady || !isNativeApp()) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const { LocalNotifications } = await import('@capacitor/local-notifications');

  // Do NOT pass sound:"default" — Capacitor resolves that to missing res/raw/default (silent).
  // Native NotificationChannels.java creates ride_alerts_v3 with the device default ringtone.
  // Recreate without sound field so we don't overwrite the native channel if it already exists.
  try {
    await PushNotifications.createChannel({
      id: CHANNEL_ALERTS,
      name: 'Ride alerts',
      description: 'Anna assigned, arrived, and trip updates',
      importance: 5,
      visibility: 1,
      vibration: true,
    });
  } catch { /* */ }

  try {
    await PushNotifications.createChannel({
      id: CHANNEL_LIVE,
      name: 'Live ride',
      description: 'Ongoing trip status while your ride is active',
      importance: 3,
      visibility: 1,
      vibration: false,
    });
  } catch { /* */ }

  try {
    await LocalNotifications.createChannel({
      id: CHANNEL_LIVE,
      name: 'Live ride',
      description: 'Ongoing trip status while your ride is active',
      importance: 3,
      visibility: 1,
      vibration: false,
    });
    await LocalNotifications.createChannel({
      id: CHANNEL_ALERTS,
      name: 'Ride alerts',
      description: 'Anna assigned, arrived, and trip updates',
      importance: 5,
      visibility: 1,
      vibration: true,
    });
  } catch { /* */ }

  channelsReady = true;
};

/**
 * Foreground status cue. The Capacitor push plugin already posts the FCM
 * notification in the foreground (with its channel sound), so this must NOT
 * post a second one — that was showing every "Anna has arrived" twice. Just
 * add a haptic so the transition is felt on silent.
 */
const alertForegroundSound = async (notification, data = {}) => {
  if (!isNativeApp()) return;
  const status = data.status;
  if (!['driver_assigned', 'arrived', 'completed', 'cancelled'].includes(status)) return;
  try {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([12, 40, 12]);
    }
  } catch { /* unsupported */ }
};

const initNativePush = async (uid, { request = true } = {}) => {
  currentPushUid = uid;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const { LocalNotifications } = await import('@capacitor/local-notifications');

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive !== 'granted') {
    if (!request || perm.receive === 'denied') return null;
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') return null;

  try {
    const localPerm = await LocalNotifications.checkPermissions();
    if (localPerm.display !== 'granted') {
      await LocalNotifications.requestPermissions();
    }
  } catch { /* */ }

  await ensureNativeChannels();

  if (!nativeListenersBound) {
    nativeListenersBound = true;
    await PushNotifications.addListener('registration', async ({ value }) => {
      if (!value) return;
      pendingToken = value;
      pendingTokenSynced = false;
      bindTokenRetry();
      if (currentPushUid) await syncPendingToken();
    });
    await PushNotifications.addListener('registrationError', (err) => {
      console.error('FCM registration error:', err);
    });
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      const data = notification?.data || {};
      if (data.clearLive === '1' || data.status === 'completed' || data.status === 'cancelled') {
        clearLiveRideNotification();
      } else if (
        data.type === 'ride_live'
        || ['driver_assigned', 'arrived', 'started'].includes(data.status)
      ) {
        updateLiveRideNotification({
          rideId: data.rideId,
          status: data.status || 'started',
          vehicleNumber: data.vehicleNumber,
          driverName: data.driverName,
          otp: data.otp,
          pickupName: data.pickupName,
          destinationName: data.destinationName,
          progress: data.progress != null ? Number(data.progress) : null,
          startedAtMs: data.startedAtMs != null ? Number(data.startedAtMs) : null,
          etaMin: data.etaMin != null ? Number(data.etaMin) : null,
          durationMin: data.durationMin != null ? Number(data.durationMin) : null,
        });
      } else if (data.status && !['driver_assigned', 'arrived', 'started'].includes(data.status)) {
        clearLiveRideNotification();
      }
      // FCM notification sound is suppressed in foreground — play via alert channel.
      if (data.type !== 'ride_live') {
        alertForegroundSound(
          { title: notification?.title, body: notification?.body },
          data,
        );
      }
      window.dispatchEvent(new CustomEvent('mt_passenger_push', { detail: notification }));
    });
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action?.notification?.data || {};
      if (data.clearLive === '1' || data.status === 'completed' || data.status === 'cancelled') {
        clearLiveRideNotification();
      }
      window.dispatchEvent(new CustomEvent('mt_push_navigate', { detail: data }));
    });
  }

  await PushNotifications.register();
  return 'native';
};

const initWebPush = async (uid, { request = true } = {}) => {
  currentPushUid = uid;
  const vapid = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  if (!vapid) {
    console.warn('Missing REACT_APP_FIREBASE_VAPID_KEY — push disabled');
    return null;
  }
  try {
    const messaging = await ensureWebMessaging();
    if (!messaging) return null;
    if (typeof Notification === 'undefined') return null;
    if (Notification.permission === 'denied') return null;
    if (Notification.permission !== 'granted') {
      if (!request) return null;
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return null;
    }
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const token = await getToken(messaging, {
      vapidKey: vapid,
      serviceWorkerRegistration: registration,
    });
    if (token) {
      pendingToken = token;
      pendingTokenSynced = false;
      bindTokenRetry();
      await syncPendingToken();
    }
    return token;
  } catch (e) {
    console.error('FCM init failed:', e);
    return null;
  }
};

/**
 * @param {string} uid
 * @param {{ request?: boolean }} opts request=false only refreshes when already allowed
 */
export const initPassengerMessaging = async (uid, opts = {}) => {
  if (!uid || typeof window === 'undefined') return null;
  const request = opts.request !== false;
  if (isNativeApp()) return initNativePush(uid, { request });
  return initWebPush(uid, { request });
};

/**
 * On logout / account switch: stop this device's token being attributed to
 * `uid` and delete it from that passenger's doc so their ride alerts (which
 * carry pickup/drop names and the trip OTP) stop being delivered here.
 */
export const clearPassengerMessaging = async (uid) => {
  // Only this device's entry is removed; the rider's other devices keep theirs.
  const device = pushDevice();
  const token = pendingToken;
  currentPushUid = null;
  pendingToken = null;
  pendingTokenSynced = false;
  try {
    const { clearFcmToken } = await import('./firebase');
    await clearFcmToken(uid, device ? { deviceId: device.deviceId, token } : null);
  } catch (e) {
    console.warn('clearPassengerMessaging: token doc cleanup failed:', e?.message);
  }
  try {
    if (!isNativeApp()) {
      const messaging = await ensureWebMessaging();
      if (messaging) {
        const { deleteToken } = await import('firebase/messaging');
        await deleteToken(messaging).catch(() => {});
      }
    }
  } catch (e) {
    console.warn('clearPassengerMessaging: token invalidation failed:', e?.message);
  }
};

export const listenForegroundMessages = (handler) => {
  if (typeof window === 'undefined') return () => {};

  const onNative = (e) => {
    const n = e.detail || {};
    handler({
      notification: {
        title: n.title || n.notification?.title,
        body: n.body || n.notification?.body,
      },
      data: n.data || {},
    });
  };
  window.addEventListener('mt_passenger_push', onNative);

  let unsubWeb = () => {};
  (async () => {
    try {
      if (isNativeApp()) return;
      const messaging = await ensureWebMessaging();
      if (!messaging) return;
      unsubWeb = onMessage(messaging, handler);
    } catch { /* */ }
  })();

  return () => {
    window.removeEventListener('mt_passenger_push', onNative);
    unsubWeb();
  };
};

export const handlePushDeepLink = (navigate) => {
  if (typeof window === 'undefined' || typeof navigate !== 'function') return () => {};
  const handler = (e) => {
    const d = e.detail?.data || e.detail || {};
    const rideId = d.rideId;
    if (!rideId) { navigate('/'); return; }
    const type = d.type || '';
    const status = d.status || '';
    if (type === 'chat') {
      navigate(`/active/${rideId}?chat=1`);
    } else if (['completed', 'cancelled'].includes(status)) {
      // Trip is over — the live trip screen just bounces; go to its record.
      navigate(`/rides/${rideId}`);
    } else {
      navigate(`/active/${rideId}`);
    }
  };
  window.addEventListener('mt_push_navigate', handler);
  return () => window.removeEventListener('mt_push_navigate', handler);
};

const shortPlace = (value, fallback = '') => {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  const first = raw.split(',')[0].trim();
  return first.length > 28 ? `${first.slice(0, 27)}…` : first;
};

/** Normalize plate: "Auto AP39B12234" (no #). */
export const formatAutoPlate = (vehicleNumber) => {
  const raw = String(vehicleNumber || '').trim().replace(/^#+\s*/, '');
  if (!raw) return '';
  const plate = raw.replace(/^auto\s*/i, '').replace(/^#+\s*/, '').trim();
  return plate ? `Auto ${plate}` : '';
};

// The fill bar is only shown once the trip has started (native hides it before
// then). Its smallest non-zero value, so it reads as "just pulled away".
const TRIP_PROGRESS_MIN = 2;

/**
 * 0–100 trip progress for the fill bar (never shown as %). This is a *true*
 * position: elapsed time vs. the current total estimate (elapsed + live ETA to
 * drop, or the booking duration when no live ETA is available). Falls back to
 * the server-sent value only when we have no timing to work with.
 *
 * An ease-out is applied to the raw time fraction so the home stretch isn't
 * under-weighted on the short 2–5 min trips that are most of Piler — e.g. "1 min
 * to drop" on a 3 min trip reads ~80% instead of ~65%, and ~93% on an 8 min
 * trip. Keep this in sync with `tripBarProgress` in firebase/functions/index.js
 * (the killed-app path).
 */
export const tripTrackProgress = ({ progress, startedAtMs, etaMin, durationMin } = {}) => {
  const startedMs = Number(startedAtMs);
  if (Number.isFinite(startedMs) && startedMs > 0) {
    const elapsedMin = Math.max(0, (Date.now() - startedMs) / 60000);
    // Guard nullish first — Number(null) is 0, which would read as "0 min to
    // drop" and slam the (monotonic) bar to full at the start of every trip,
    // before the first live ETA resolves.
    const etaNum = etaMin == null || etaMin === '' ? NaN : Number(etaMin);
    const eta = Number.isFinite(etaNum) && etaNum >= 0 ? etaNum : null;
    const totalEst = eta != null
      ? elapsedMin + eta
      : (Number(durationMin) > 0 ? Number(durationMin) : 0);
    if (totalEst > 0) {
      const frac = Math.max(0, Math.min(1, elapsedMin / totalEst));
      const eased = 1 - Math.pow(1 - frac, 1.5); // ease-out — lift the mid/late trip
      const pct = TRIP_PROGRESS_MIN + eased * (97 - TRIP_PROGRESS_MIN);
      return Math.round(Math.max(TRIP_PROGRESS_MIN, Math.min(97, pct)));
    }
  }
  if (progress != null && Number.isFinite(Number(progress))) {
    return Math.max(TRIP_PROGRESS_MIN, Math.min(97, Math.round(Number(progress))));
  }
  return TRIP_PROGRESS_MIN;
};

const LIVE_STATUSES = new Set(['driver_assigned', 'arrived', 'started']);

// Monotonic-guard state for the in-trip progress bar (per ride).
const liveProgressState = { rideId: null, last: 0 };

const liveStatusCopy = (status, driverName, otp, etaMin) => {
  const name = String(driverName || '').trim();
  const otpStr = String(otp || '').trim();
  const eta = Number(etaMin) > 0 ? Math.round(Number(etaMin)) : null;
  if (status === 'driver_assigned') {
    const lead = eta ? `Anna ${eta} min away` : (name ? `${name} is coming to you` : 'Anna is on the way');
    return {
      title: 'Anna on the way',
      statusLine: otpStr ? `${lead} · OTP: ${otpStr}` : lead,
      progress: 0, // bar is hidden before the trip starts
    };
  }
  if (status === 'arrived') {
    return {
      title: 'Anna has arrived',
      statusLine: otpStr ? `Share OTP: ${otpStr}` : (name ? `${name} is at pickup` : 'Anna is at your pickup'),
      progress: 0, // bar is hidden before the trip starts
    };
  }
  const base = name ? `Trip in progress · ${name}` : 'Trip in progress';
  return {
    title: 'Trip in progress',
    statusLine: eta ? `${eta} min to drop · ${name || 'on the way'}` : base,
    progress: null,
  };
};

/** Sticky lock-screen card for assigned / arrived / started. */
export const updateLiveRideNotification = async (info = {}) => {
  if (!isNativeApp()) return;
  if (!LIVE_STATUSES.has(info.status)) {
    await clearLiveRideNotification();
    return;
  }
  try {
    await ensureNativeChannels();
    const pickupLabel = shortPlace(info.pickupName, 'Pickup');
    const dropLabel = shortPlace(info.destinationName || info.dropName, 'Drop');
    const plateLine = formatAutoPlate(info.vehicleNumber);
    const etaMin = Number(info.etaMin) > 0
      ? Number(info.etaMin)
      : (Number(info.remainingM) > 0 ? Math.max(1, Math.round((info.remainingM / 1000 / 22) * 60)) : null);
    const copy = liveStatusCopy(info.status, info.driverName, info.otp, etaMin);
    let progress = copy.progress != null
      ? copy.progress
      : tripTrackProgress({
        progress: info.progress,
        startedAtMs: info.startedAtMs,
        etaMin,
        durationMin: info.durationMin,
      });
    // Never let the trip bar slide backwards on a noisy ETA re-estimate.
    if (info.status === 'started') {
      if (liveProgressState.rideId !== info.rideId) {
        liveProgressState.rideId = info.rideId;
        liveProgressState.last = 0;
      }
      progress = Math.max(progress, liveProgressState.last);
      liveProgressState.last = progress;
    } else {
      liveProgressState.rideId = info.rideId;
      liveProgressState.last = 0;
    }
    const body = [copy.statusLine, plateLine].filter(Boolean).join(' · ');
    const plugin = await getRideLivePlugin();

    if (plugin?.update) {
      await plugin.update({
        title: copy.title,
        body,
        statusLine: copy.statusLine,
        plateLine,
        pickupLabel,
        dropLabel,
        rideId: info.rideId || '',
        status: info.status || 'started',
        progress,
      });
      return;
    }

    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.schedule({
      notifications: [{
        id: LIVE_NOTIF_ID,
        title: copy.title,
        body,
        channelId: CHANNEL_LIVE,
        ongoing: true,
        autoCancel: false,
        smallIcon: 'ic_stat_rides',
        largeIcon: 'ic_launcher',
        iconColor: '#00A86B',
        largeBody: body,
        summaryText: 'Live trip',
        extra: {
          rideId: info.rideId || '',
          type: 'ride_live',
          status: info.status,
        },
      }],
    });
  } catch (e) {
    console.warn('live notification update failed:', e?.message || e);
  }
};

export const clearLiveRideNotification = async () => {
  if (!isNativeApp()) return;
  try {
    const plugin = await getRideLivePlugin();
    if (plugin?.clear) {
      await plugin.clear();
      return;
    }
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.cancel({ notifications: [{ id: LIVE_NOTIF_ID }] });
  } catch { /* */ }
};
